import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import * as MicrosoftGraph from "@microsoft/microsoft-graph-client";
import globals from "./globals";

//Publicly available: https://login.windows.net/pulteney.sa.edu.au/.well-known/openid-configuration
var PULTENEY_TENANT_ID = 'ebc0ee77-ff3b-4903-bce7-6cb50ce73fd4'

var msalConfig = {
    auth: {
        clientId: '3b87bd3f-2e6b-44f4-afba-39ebc4cd11b4',
        //The actual URL for redirectUri doesn't really matter.
        //But if it's not valid and you are hosting on SharePoint, it will not work.
        //So we just use a static file that is always available.
        redirectUri: location.origin + '/favicon/favicon.png',
        authority: "https://login.microsoftonline.com/organizations"
    },
    cache: {
        // Pinned to @azure/msal-browser v3 on purpose — do not bump to v4.
        // v4 encrypts this cache with a key held in a *session* cookie
        // (msal.cache.encryption), so quitting the browser throws away the
        // account and everyone is asked to log in again on the next launch.
        // The only escape hatch is the "Stay signed in?" (KMSI) claim, which
        // is a tenant-wide Entra setting we don't control. See v3.30.0's
        // cache/LocalStorage.mjs: a plain window.localStorage wrapper.
        cacheLocation: "localStorage"
    },
};

var msalRequest = {
    scopes: ["user.read", "Files.Read.All", "User.ReadBasic.All", "Sites.Read.All", "offline_access"]
}

export const msalClient = new PublicClientApplication(msalConfig);

// MSAL only ever allows one interactive flow at a time, tracked by a single
// `msal.interaction.status` key. Because cacheLocation is localStorage, MSAL *reads*
// that key from localStorage as a legacy fallback but only ever *deletes* it from
// sessionStorage, so a copy left behind by an older MSAL wedges every future
// loginPopup with `interaction_in_progress` forever. Sweep it before initialize().
try {
    localStorage.removeItem('msal.interaction.status');
} catch (e) {
    console.warn('Could not clear legacy msal.interaction.status', e);
}

// MSAL v3+ requires initialize() to complete before any other API call
// (loginPopup / acquireTokenSilent / getAllAccounts all throw otherwise). Kick it
// off once at module load and await this promise before touching MSAL anywhere.
//
// handleRedirectPromise() is chained on because it is the only thing that clears
// `msal.interaction.status`. logoutRedirect() sets that flag and navigates the tab
// away; sessionStorage survives the round trip back from Microsoft, so without this
// call the flag stays set and every subsequent Sign in throws interaction_in_progress
// for the life of the tab (a reload does not help). With no auth hash in the URL it
// simply cleans up the stale interaction state and resolves null.
export const msalReady = msalClient.initialize()
    .then(() => msalClient.handleRedirectPromise())
    .catch(e => console.error('handleRedirectPromise failed', e));

export async function signIn(logCallback) {
    if (!logCallback) logCallback = function () { }
    // Login
    try {
        await msalReady;
        // Use MSAL to login
        logCallback('Opening login window');
        await msalClient.loginPopup(msalRequest);
        console.log('id_token acquired at: ' + new Date().toString());

        logCallback('Logged in to Office 365. Now to check that we can access the timetable...');
        console.log('Login successful!')
        if (location.pathname == '/login') {
            console.log('We are on the login page, about to verify that our token works')
            logCallback("The timetable works! Let's load up the Speedy web app.");
            var token = await getAccessToken();
            return token;
        }
    } catch (error) {
        if (error.errorCode == 'user_cancelled') {
            logCallback('The login popup window was closed. Perhaps popups are blocked?', 'ERROR');
        } else {
            logCallback(error, 'ERROR');
        }
        console.error(error);
        throw error;
    }
}

async function getAccessToken() {
    await msalReady;
    var account = getPGSAccount();

    if (!account) {
        globals.error = 'Could not get timetable data. Click here to try logging in again.';
        throw new Error("No PGS account found.");
    }

    try {
        // First, attempt to get the token silently
        const silentRequest = {
            scopes: msalRequest.scopes,
            account: account
        };

        const silentResult = await msalClient.acquireTokenSilent(silentRequest);
        globals.error = null;
        return silentResult.accessToken;
    } catch (silentError) {
        // If silent requests fails with InteractionRequiredAuthError,
        // attempt to get the token interactively
        if (silentError instanceof InteractionRequiredAuthError) {
            try {
                const interactiveResult = await msalClient.acquireTokenPopup(msalRequest);
                globals.error = null;
                return interactiveResult.accessToken;
            } catch (popupError) {
                console.error('error in acquireTokenPopup:', popupError);
                globals.error = 'Could not get timetable data. Click here to try logging in again.';
                throw popupError;
            }
        } else {
            console.error('error in getAccessToken:', silentError);
            globals.error = 'Could not get timetable data. Click here to try logging in again.';
            throw silentError;
        }
    }
}

function getPGSAccount() {
    const currentAccounts = msalClient.getAllAccounts();
    if (!currentAccounts || currentAccounts.length === 0) {
        // No user signed in
        console.log('No user signed in');
        return null;
    } else {
        var pgsAccount = currentAccounts.find(account =>
            account.tenantId === PULTENEY_TENANT_ID
        )
        if (!pgsAccount) {
            console.warn('User is logged into account(s), but none are PGS');
        }
        return pgsAccount;
    }
}

// Create an authentication provider
const authProvider = {
    getAccessToken
};

// Initialize the Graph client
export const graphClient = MicrosoftGraph.Client.initWithMiddleware({ authProvider });
